import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit-html/directives/style-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { getIconLibrary, unwatchIcon, watchIcon } from './library';
import { requestIcon } from './request';
import encapsulatedStyles from './styles/outline-icon.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

// DOMParser instance for parsing SVG content.
const parser = new DOMParser();

// Type definition for supported icon library names.
type IconLibraryName =
  | 'bootstrap'
  | 'boxicons'
  | 'custom'
  | 'fa'
  | 'fa5'
  | 'heroicons'
  | 'iconoir'
  | 'ionicons'
  | 'jam'
  | 'legacy'
  | 'lucide'
  | 'material'
  | 'remix'
  | 'unicons';

// Type definition for size options
type IconSize = 'full-width' | 'full-size' | string | boolean;

/**
 * OutlineIcon is a web component for rendering SVG icons from various icon libraries.
 *
 * @since 1.0
 * @status stable
 *
 * This icon component is capable of loading the following libraries:
 * bootstrap, boxicons, custom, fontawesome, fa5, heroicons, iconoir, ionicons, jam, legacy, lucide, material, remix, and unicons. (the IconLibraryName type).
 * For the addition of project-specific icons, add them to either custom icons or legacy icons.
 * Icons are loaded on demand, excluding legacy icons which are always available and use of which should be avoided.
 * See readme.md for full documentation.
 *
 *
 * @event outline-error - Emitted when there is an error loading the icon.
 *
 * @attr {string} name - The name of the icon to display.
 * @attr {string} src - The external URL of an SVG file.
 * @attr {string|boolean} label - Accessible label for the icon. If omitted, the icon is hidden from assistive devices.
 * @attr {IconLibraryName} library - The name of the icon library to use.
 * @attr {IconSize} size - The size of the icon. Can be:
 *   - A number in pixels (e.g., "24")
 *   - "full-width" (sets width to 100% with auto height)
 *   - "full-size" (sets both width and height to 100%)
 */
@customElement('outline-icon')
export class OutlineIcon extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    encapsulatedCSS: encapsulatedStyles,
  });

  // State to store the SVG content.
  @state() private svg = '';

  /**
   * The name of the icon to draw.
   * @param name
   */
  @property({ type: String, reflect: true, attribute: 'name' })
  name?: string;

  /**
   * An external URL of an SVG file.
   * @param src
   *
   * WARNING: Be sure you trust the content you are including as it will be executed as code and can result in XSS attacks.
   */
  @property({ type: String, attribute: 'src' })
  src?: string;

  /**
   * An alternate description to use for accessibility. If omitted, the icon will be ignored by assistive devices.
   * @param label
   *
   * @todo: Make this default to "true" and be turned off when an icon is decorative.
   */
  @property({ attribute: 'label' })
  label: string | boolean = false;

  /**
   * The name of a registered custom icon library.
   * @param library
   */
  @property({ type: String, attribute: 'library' })
  library?: IconLibraryName;

  /**
   * The size of the icon. Can be:
   * - A number in pixels (e.g., "24")
   * - "full-width" (sets width to 100% with auto height)
   * - "full-size" (sets both width and height to 100%)
   * @param size
   */
  @property({ attribute: 'size' })
  size: IconSize;

  private getSizeStyles() {
    if (!this.size) return {};

    switch (this.size) {
      case 'full-width':
        return { width: '100%' };
      case 'full-size':
        return { width: '100%', height: '100%' };
      default:
        if (typeof this.size === 'string') {
          return { width: this.size, height: this.size };
        }
        return {};
    }
  }

  // Lifecycle callback that runs when the component is added to the DOM.
  connectedCallback() {
    super.connectedCallback();
    watchIcon(this);
  }

  // Lifecycle callback that runs after the component's first render.
  firstUpdated() {
    this.setIcon();
  }

  // Lifecycle callback that runs after the component's properties or state have changed.
  updated() {
    this.setIcon();
  }

  // Lifecycle callback that runs when the component is removed from the DOM.
  disconnectedCallback() {
    super.disconnectedCallback();
    unwatchIcon(this);
  }

  // Callback that runs when an attribute of the component has changed.
  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ) {
    super.attributeChangedCallback(name, oldValue, newValue);
    // Perform the update if the attribute change is relevant.
    if (['name', 'src', 'library'].includes(name)) {
      this.setIcon();
    }
  }

  // Emits a custom event with specified name and options.
  private emit(name: string, options?: CustomEventInit) {
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: false,
      composed: true,
      detail: {},
      ...options,
    });
    this.dispatchEvent(event);
    return event;
  }

  // Resolves the URL for the icon based on the 'name' and 'library' attributes or 'src' attribute.
  private async getUrl() {
    const library = getIconLibrary(this.library);
    if (this.name && library) {
      const resolverResult = library.resolver(this.name);
      if (typeof resolverResult === 'function') {
        return await resolverResult();
      }
      return resolverResult;
    }
    return this.src;
  }

  /**
   * @internal Fetches the icon and redraws it.
   * Used to handle library registrations. This is also called outside this class.
   */
  redraw() {
    this.setIcon();
  }

  async setIcon() {
    const library = getIconLibrary(this.library);
    const url = await this.getUrl();
    if (url) {
      try {
        const file = await requestIcon(url);
        if (url !== (await this.getUrl())) {
          return; // Ignore if the URL has changed during the request.
        }
        if (file.ok) {
          const doc = parser.parseFromString(file.svg, 'text/html');
          const svgEl = doc.body.querySelector('svg');

          if (svgEl !== null) {
            library?.mutator?.(svgEl);
            this.svg = svgEl.outerHTML;
          } else {
            this.svg = '';
            this.emit('outline-error', { detail: { status: file.status } });
          }
        } else {
          this.svg = '';
          this.emit('outline-error', { detail: { status: file.status } });
        }
      } catch {
        this.emit('outline-error', { detail: { status: -1 } });
      }
    } else if (this.svg.length > 0) {
      // If we can't resolve a URL and an icon was previously set, remove it
      this.svg = '';
    }
  }

  // Renders the component's HTML template.
  render() {
    const hasLabel = typeof this.label === 'string' && this.label.length > 0;
    const wrapperClasses = {
      icon: true,
      [`icon-library--${this.library}`]: Boolean(this.library),
    };

    return html` <div
      part="base"
      class="${classMap(wrapperClasses)}"
      role=${ifDefined(hasLabel ? 'img' : undefined)}
      aria-label=${ifDefined(hasLabel ? this.label : undefined)}
      style=${styleMap(this.getSizeStyles())}
    >
      ${unsafeSVG(this.svg)}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-icon': OutlineIcon;
  }
}
